import { useState, useCallback } from 'react';
import { PictureApiService } from '../lib/pictureApiService';
import { FileValidator, DEFAULT_IMAGE_VALIDATION_RULES } from '../lib/fileValidator';

interface EntityPictureState {
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  isDragOver: boolean;
  imageUrl: string | null;
  lastModified: string | null;
}

export interface UseEntityPictureOptions {
  maxFileSize?: number; // in MB
  acceptedFormats?: string[];
  autoRetryCount?: number;
  enabled?: boolean; // Whether to attempt loading the picture
}

export const useEntityPicture = (
  entityType: 'organization' | 'asset' | 'user',
  entityId: string,
  orgCode: string,
  options: UseEntityPictureOptions = {}
) => {
  const [state, setState] = useState<EntityPictureState>({
    isLoading: false,
    isUploading: false,
    uploadProgress: 0,
    error: null,
    isDragOver: false,
    imageUrl: null,
    lastModified: null,
  });

  const {
    maxFileSize = 10,
    acceptedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'],
    autoRetryCount = 3,
    enabled = true,
  } = options;

  const updateState = useCallback((updates: Partial<EntityPictureState>) => {
    setState(prev => {
      // Clean up old blob URLs to prevent memory leaks
      if (updates.imageUrl !== prev.imageUrl && prev.imageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imageUrl);
      }
      return { ...prev, ...updates };
    });
  }, []);

  const loadPicture = useCallback(async (size?: string, documentUid?: string) => {
    if (!enabled) {
      updateState({
        isLoading: false,
        error: null,
        imageUrl: null
      });
      return;
    }

    updateState({ isLoading: true, error: null });

    try {
      // Try to actually fetch the picture to verify it exists
      const blob = await PictureApiService.getPicture(entityType, orgCode, entityId, size, documentUid);
      const imageUrl = URL.createObjectURL(blob);

      updateState({
        imageUrl,
        isLoading: false,
        lastModified: new Date().toISOString()
      });
    } catch {
      // If picture doesn't exist or fails to load, set imageUrl to null to show fallback
      updateState({
        error: null, // Don't show error for missing pictures, just show fallback
        isLoading: false,
        imageUrl: null
      });
    }
  }, [entityType, orgCode, entityId, updateState, enabled]);

  const uploadPicture = useCallback(async (file: File, retryCount = 0): Promise<string> => {
    // Validate file
    const validationRules = {
      ...DEFAULT_IMAGE_VALIDATION_RULES,
      maxSizeBytes: maxFileSize * 1024 * 1024,
      allowedTypes: acceptedFormats,
    };

    const validationError = await FileValidator.validateImageFile(file, validationRules);
    if (validationError) {
      updateState({ error: validationError });
      throw new Error(validationError);
    }

    updateState({
      isUploading: true,
      uploadProgress: 0,
      error: null
    });

    try {
      const uploadResult = await PictureApiService.uploadPicture(
        entityType,
        orgCode,
        entityId,
        file,
        (progress) => updateState({ uploadProgress: progress })
      );

      updateState({
        isUploading: false,
        uploadProgress: 100,
        lastModified: new Date().toISOString()
      });

      // Reload the picture after successful upload with the new document_uid
      await loadPicture(undefined, uploadResult.document_uid);

      return uploadResult.document_uid;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';

      // Auto-retry on transient failures
      if (retryCount < autoRetryCount && errorMessage.includes('failed')) {
        setTimeout(() => {
          uploadPicture(file, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return '';
      }

      updateState({
        error: errorMessage,
        isUploading: false,
        uploadProgress: 0
      });
      throw err;
    }
  }, [entityType, orgCode, entityId, maxFileSize, acceptedFormats, autoRetryCount, updateState, loadPicture]);

  const deletePicture = useCallback(async () => {
    updateState({ isLoading: true, error: null });

    try {
      await PictureApiService.deletePicture(entityType, orgCode, entityId);

      updateState({
        imageUrl: null,
        isLoading: false,
        lastModified: new Date().toISOString()
      });
    } catch (err) {
      updateState({
        error: err instanceof Error ? err.message : 'Failed to delete picture',
        isLoading: false
      });
      throw err;
    }
  }, [entityType, orgCode, entityId, updateState]);

  const setDragOver = useCallback((isDragOver: boolean) => {
    updateState({ isDragOver });
  }, [updateState]);

  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  const cleanup = useCallback(() => {
    // Clean up any blob URLs when component unmounts
    setState(prev => {
      if (prev.imageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.imageUrl);
      }
      return prev;
    });
  }, []);

  return {
    state,
    uploadPicture,
    deletePicture,
    loadPicture,
    setDragOver,
    clearError,
    cleanup,
  };
};